import { parseToolbarQueryParam } from '../config/pacsConfig'
import {
  EMBED_QUERY_KEY,
  INVITE_QUERY_KEY,
  PERSONA_QUERY_KEY,
  EDGE_ID_QUERY_KEY,
  ELEV_QUERY_KEY,
  TICKET_PURPOSE,
  PLAN_QUERY_KEY,
  PLAN_RANK,
  PORTAL_QUERY_KEY,
  SHELL_QUERY_KEY,
  TICKET_QUERY_KEY,
  TOKEN_QUERY_KEY,
  WORKLIST_QUERY_KEY,
  UDV_PERSONAS,
  UDV_PLANS,
} from './constants'

/**
 * @param {string|null|undefined} raw
 * @returns {'patient'|'demo'|'lite'|'pro'|'premium'|'supremo'|null}
 */
export function parsePlanQueryParam(raw) {
  if (raw == null || raw === '') return null
  const v = String(raw).trim().toLowerCase()
  if (
    v === UDV_PLANS.PATIENT ||
    v === UDV_PLANS.DEMO ||
    v === UDV_PLANS.LITE ||
    v === UDV_PLANS.PRO ||
    v === UDV_PLANS.PREMIUM ||
    v === UDV_PLANS.SUPREMO
  ) {
    return v
  }
  return null
}

/**
 * Alias preferido `shell=` + compat `toolbar=` / `toolbarLayout=`.
 * @param {URLSearchParams|null|undefined} params
 * @returns {string|null} TOOLBAR_LAYOUTS value
 */
export function parseShellFromSearchParams(params) {
  if (!params || typeof params.get !== 'function') return null
  const shell = params.get(SHELL_QUERY_KEY)
  if (shell) {
    const parsed = parseToolbarQueryParam(shell)
    if (parsed) return parsed
  }
  return parseToolbarQueryParam(
    params.get('toolbar') || params.get('toolbarLayout')
  )
}

/**
 * @param {URLSearchParams|null|undefined} params
 * @returns {string|null}
 */
export function parseTokenFromSearchParams(params) {
  if (!params || typeof params.get !== 'function') return null
  const t = params.get(TOKEN_QUERY_KEY)
  if (t == null || String(t).trim() === '') return null
  return String(t).trim()
}

/**
 * @param {URLSearchParams|null|undefined} params
 * @returns {string|null}
 */
export function parseTicketFromSearchParams(params) {
  if (!params || typeof params.get !== 'function') return null
  const t = params.get(TICKET_QUERY_KEY)
  if (t == null || String(t).trim() === '') return null
  return String(t).trim()
}

/**
 * @param {URLSearchParams|null|undefined} params
 * @returns {string|null}
 */
export function parseWorklistFromSearchParams(params) {
  if (!params || typeof params.get !== 'function') return null
  const w = params.get(WORKLIST_QUERY_KEY)
  if (w == null || String(w).trim() === '') return null
  return String(w).trim()
}

/**
 * @param {URLSearchParams|null|undefined} params
 * @returns {'lite'|'pro'|'premium'|null}
 */
export function parsePlanFromSearchParams(params) {
  if (!params || typeof params.get !== 'function') return null
  return parsePlanQueryParam(params.get(PLAN_QUERY_KEY))
}

/**
 * @param {string|null|undefined} raw
 * @returns {boolean|null} true/false si viene en query; null si ausente/inválido
 */
export function parsePortalQueryParam(raw) {
  if (raw == null || raw === '') return null
  const v = String(raw).trim().toLowerCase()
  if (v === '1' || v === 'true' || v === 'on' || v === 'yes') return true
  if (v === '0' || v === 'false' || v === 'off' || v === 'no') return false
  return null
}

/**
 * @param {URLSearchParams|null|undefined} params
 * @returns {boolean|null}
 */
export function parsePortalFromSearchParams(params) {
  if (!params || typeof params.get !== 'function') return null
  return parsePortalQueryParam(params.get(PORTAL_QUERY_KEY))
}

/**
 * @param {URLSearchParams|null|undefined} params
 * @returns {string|null}
 */
export function parseInviteFromSearchParams(params) {
  if (!params || typeof params.get !== 'function') return null
  const t = params.get(INVITE_QUERY_KEY)
  if (t == null || String(t).trim() === '') return null
  return String(t).trim()
}

/**
 * @param {string|null|undefined} raw
 * @returns {'portal'|'clinical'|null}
 */
export function parsePersonaQueryParam(raw) {
  if (raw == null || raw === '') return null
  const v = String(raw).trim().toLowerCase()
  if (v === UDV_PERSONAS.PORTAL || v === UDV_PERSONAS.CLINICAL) return v
  return null
}

/**
 * @param {URLSearchParams|null|undefined} params
 * @returns {'portal'|'clinical'|null}
 */
export function parsePersonaFromSearchParams(params) {
  if (!params || typeof params.get !== 'function') return null
  return parsePersonaQueryParam(params.get(PERSONA_QUERY_KEY))
}

/**
 * Origen clínica / Edge (Controller edge_id) en el link del estudio.
 * @param {URLSearchParams|null|undefined} params
 * @returns {string|null}
 */
export function parseEdgeIdFromSearchParams(params) {
  if (!params || typeof params.get !== 'function') return null
  const raw = params.get(EDGE_ID_QUERY_KEY)
  if (raw == null || String(raw).trim() === '') return null
  return String(raw).trim()
}

/**
 * JWT one-shot `elev` en redirect clínico plataforma → UDV.
 * @param {URLSearchParams|null|undefined} params
 * @returns {string|null}
 */
export function parseElevationTokenFromSearchParams(params) {
  if (!params || typeof params.get !== 'function') return null
  const raw = params.get(ELEV_QUERY_KEY)
  if (raw == null || String(raw).trim() === '') return null
  return String(raw).trim()
}

/**
 * Hint embed (`embed=workspace`). No autoriza plan.
 * @param {URLSearchParams|null|undefined} params
 * @returns {'workspace'|null}
 */
export function parseEmbedFromSearchParams(params) {
  if (!params || typeof params.get !== 'function') return null
  const raw = params.get(EMBED_QUERY_KEY)
  if (raw == null || String(raw).trim() === '') return null
  const v = String(raw).trim().toLowerCase()
  if (v === 'workspace') return 'workspace'
  return null
}

/**
 * @param {URLSearchParams|null|undefined} params
 * @returns {boolean}
 */
export function parseHideFullscreenPromptFromSearchParams(params) {
  if (!params || typeof params.get !== 'function') return false
  const raw = params.get('hideFullscreenPrompt')
  if (raw == null || String(raw).trim() === '') return false
  const v = String(raw).trim().toLowerCase()
  return v === '1' || v === 'true' || v === 'on' || v === 'yes'
}

/**
 * @param {string|null|undefined} raw
 * @returns {'patient_share'|'clinical'|null}
 */
export function parseTicketPurposeParam(raw) {
  if (raw == null || raw === '') return null
  const v = String(raw).trim().toLowerCase()
  if (v === TICKET_PURPOSE.PATIENT_SHARE || v === TICKET_PURPOSE.CLINICAL) {
    return v
  }
  return null
}

/**
 * @param {'demo'|'lite'|'pro'|'premium'} plan
 * @param {'demo'|'lite'|'pro'|'premium'} maxPlan
 * @returns {'demo'|'lite'|'pro'|'premium'}
 */
export function clampPlan(plan, maxPlan) {
  const p = PLAN_RANK[plan] || 0
  const m = PLAN_RANK[maxPlan] || 0
  if (plan === UDV_PLANS.SUPREMO && maxPlan !== UDV_PLANS.SUPREMO) {
    return maxPlan || UDV_PLANS.PREMIUM
  }
  if (!p) return maxPlan || UDV_PLANS.DEMO
  if (!m) return plan
  if (p <= m) return plan
  return maxPlan
}

/**
 * Shell corto (classic|edge|studio|ohif|oviyamng) desde valor TOOLBAR_LAYOUTS.
 * @param {string|null|undefined} layout
 * @returns {'classic'|'edge'|'studio'|'ohif'|'oviyamng'|null}
 */
export function shellShortFromLayout(layout) {
  if (!layout) return null
  const s = String(layout)
  const lower = s.toLowerCase()
  if (s.indexOf('Studio') >= 0 || lower.indexOf('studio') >= 0) {
    return 'studio'
  }
  if (s.indexOf('Ohif') >= 0 || lower.indexOf('ohif') >= 0) {
    return 'ohif'
  }
  if (
    lower.indexOf('oviyamng') >= 0 ||
    lower.indexOf('oviyam-ng') >= 0 ||
    s.indexOf('OviyamNg') >= 0 ||
    s.indexOf('OviyamNG') >= 0 ||
    s.indexOf('Oviyam') >= 0 ||
    lower.indexOf('oviyam') >= 0
  ) {
    return 'oviyamng'
  }
  if (s.indexOf('Edge') >= 0 || lower.indexOf('edge') >= 0) {
    return 'edge'
  }
  if (s.indexOf('Classic') >= 0 || lower.indexOf('classic') >= 0) {
    return 'classic'
  }
  return null
}
